import './styles.css';
import { generateLevel, tierForRun } from './core/generator';
import { decodeLevel } from './core/sharecode';
import { store } from './game/store';
import { LEVELS } from './levels';
import { type Screen, app, markReady } from './ui/app';
import { openHelp } from './ui/dialogs';
import { ICONS, h, icon } from './ui/dom';
import { editorScreen } from './ui/editor';
import { gameScreen } from './ui/game';
import { levelsScreen } from './ui/levels';
import { titleScreen } from './ui/title';

/** A designed error state with a way out. */
function errorScreen(titleText: string, message: string): Screen {
  return {
    title: titleText,
    scroll: true,
    mount(root) {
      root.append(
        h(
          'div',
          { class: 'error-panel', role: 'alert' },
          h('h1', { class: 'wordmark', text: 'Lensing' }),
          h('h2', { text: titleText }),
          h('p', { text: message }),
          h(
            'p',
            { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' } },
            h('a', { class: 'btn primary', href: '#/' }, icon(ICONS.back), 'Back to the title'),
            h('a', { class: 'btn', href: '#/levels', text: 'Levels' }),
            h('a', { class: 'btn', href: '#/editor', text: 'Open the editor' }),
          ),
        ),
      );
      markReady();
    },
    unmount() {},
    frame() {
      app.renderer.renderPaper(store.settings.contrast);
      return false;
    },
  };
}

function route(hash: string): Screen {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const [page = '', a, b] = parts;
  switch (page) {
    case '':
      return titleScreen();
    case 'about': {
      const s = titleScreen();
      const mount = s.mount.bind(s);
      s.mount = (root) => {
        mount(root);
        openHelp('physics');
      };
      return s;
    }
    case 'levels':
      return levelsScreen();
    case 'play': {
      const level = LEVELS.find((l) => l.id === a);
      if (!level)
        return errorScreen(
          'No such plate',
          `There is no level "${a ?? ''}". Pick one from the level list.`,
        );
      return gameScreen({ mode: 'campaign', level });
    }
    case 'endless': {
      let seed = Number(a);
      let tier = Number(b);
      if (!Number.isInteger(seed) || seed < 0) seed = Math.floor(Math.random() * 1e9);
      if (!Number.isInteger(tier) || tier < 1 || tier > 6)
        tier = tierForRun(store.progress.endless.streak);
      // Make the address shareable without adding a history entry.
      if (a !== String(seed) || b !== String(tier))
        history.replaceState(null, '', `#/endless/${seed}/${tier}`);
      return gameScreen({
        mode: 'endless',
        level: generateLevel(seed, tier),
        endless: { seed, tier },
      });
    }
    case 'custom': {
      const r = decodeLevel(a ?? '');
      if (!r.ok)
        return errorScreen(
          'This share code does not work',
          `It could not be read: ${r.error}. Ask for the link again, or build your own plate in the editor.`,
        );
      return gameScreen({ mode: 'custom', level: r.level, code: a });
    }
    case 'editor':
      return editorScreen(a ?? null);
    default:
      return errorScreen('Page not found', 'That address does not lead anywhere in Lensing.');
  }
}

app.init(route);
